<template>
  <div v-if="draft" class="p-4 min-h-screen bg-gray-100">
    <!-- Wider than the 6xl the other outreach pages use: this one carries three
         columns rather than one, and the editor is the column that suffers when
         they share 6xl. -->
    <div class="bg-white border border-gray-200 rounded-lg shadow-sm p-4 max-w-7xl mx-auto">
    <div class="flex items-center justify-between mb-3 pb-3 border-b border-gray-100">
      <button @click="goBack" class="text-sm text-gray-600 hover:text-gray-900">← Back</button>
      <div class="flex items-center gap-2">
        <span :class="statusClass(draft.status)" class="px-2 py-0.5 rounded-full text-xs">{{ statusLabel(draft.status) }}</span>
        <button
          v-if="draft.status === 'sent'"
          @click="viewSent"
          class="px-3 py-1 rounded-md text-sm border border-gray-300 bg-white hover:bg-gray-100"
        >View as sent email</button>
        <!-- One action. The modal asks what to start from: nothing (write it
             yourself) or a template. -->
        <button
          v-if="draft.status === 'sent' && canFollowUp"
          @click="showFollowUpModal = true"
          :disabled="followingUp"
          class="px-3 py-1 rounded-md text-sm border border-gray-300 bg-white hover:bg-gray-100 disabled:opacity-40"
        >{{ followingUp ? 'Creating…' : 'Reply' }}</button>
        <span v-if="saving" class="text-xs text-gray-400">Saving…</span>
        <span v-if="readOnly" class="px-2 py-0.5 rounded-full text-xs bg-amber-100 text-amber-800">View only</span>
        <!-- A discarded draft can't be sent or re-discarded; offer a fresh start
             from the same company + template instead. -->
        <template v-if="!readOnly">
        <button
          v-if="draft.status === 'discarded'"
          @click="createNew"
          :disabled="recreating"
          class="px-3 py-1 bg-dark-primary text-white rounded-md text-sm disabled:opacity-40"
        >{{ recreating ? 'Creating…' : 'Create new draft' }}</button>
        <template v-else-if="draft.status === 'scheduled'">
          <button
            @click="unschedule"
            :disabled="unscheduling"
            class="px-3 py-1 rounded-md text-sm border border-gray-300 bg-white hover:bg-gray-100 disabled:opacity-40"
          >{{ unscheduling ? 'Cancelling…' : 'Unschedule' }}</button>
        </template>
        <template v-else>
          <button @click="discard" class="px-3 py-1 rounded-md text-sm border border-red-300 bg-red-50 text-red-700 hover:bg-red-100">Discard</button>
          <!-- Scheduling commits to the same CRM changes, just later, so it
               flags the pane exactly as Send does. -->
          <button
            v-if="draft.status !== 'sent'"
            @click="showScheduleModal = true"
            @mouseenter="sendHover = true"
            @mouseleave="sendHover = false"
            :disabled="sending || !recipient || subjectMissing"
            class="px-3 py-1 rounded-md text-sm border border-gray-300 bg-white hover:bg-gray-100 disabled:opacity-40"
          >Schedule send</button>
          <button
            @click="send"
            @mouseenter="sendHover = true"
            @mouseleave="sendHover = false"
            :disabled="sending || !recipient || subjectMissing || draft.status === 'sent'"
            class="px-3 py-1 bg-dark-primary text-white rounded-md text-sm disabled:opacity-40"
          >Send</button>
        </template>
        </template>
      </div>
    </div>

    <div
      v-if="readOnly"
      class="mb-3 rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-800"
    >
      Opened through Supervisor Mode. You can't send from {{ draft.from_email || "this draft's mailbox" }}, so this draft is view only.
    </div>
    <div
      v-if="isScheduled"
      class="mb-3 flex items-center gap-2 rounded-md border border-indigo-200 bg-indigo-50 px-3 py-2 text-sm text-indigo-800"
    >
      <span class="font-semibold shrink-0">⏱ Scheduled</span>
      <span class="text-indigo-700">
        Sends automatically {{ formatDate(draft.scheduled_send_at) }}. Unschedule to make changes.
      </span>
    </div>

    <!-- company context | editor | CRM actions -->
    <div class="grid grid-cols-4 gap-4">
      <!-- Reference panel (replaces the old in-body TO-DELETE block) -->
      <aside class="col-span-1 bg-gray-50 border border-gray-200 rounded-md p-3 text-sm" :class="{ 'pointer-events-none opacity-60': readOnly }">
        <!-- AI suggestions for [TOKEN] placeholders -->
        <div class="mb-3 pb-3 border-b border-gray-200">
          <div class="flex items-center justify-between mb-2">
            <div class="flex items-center gap-1">
              <h2 class="font-semibold text-gray-700">AI Suggestions</h2>
              <span class="relative group flex items-center">
                <InformationCircleIcon class="w-4 h-4 text-gray-400 cursor-help" />
                <div class="pointer-events-none absolute left-0 top-6 z-30 hidden group-hover:block w-72 p-3 rounded-md bg-gray-800 text-white text-xs font-normal leading-relaxed shadow-lg">
                  For each <span class="font-mono">[TOKEN]</span> placeholder in the draft (e.g.
                  <span class="font-mono">[PRODUCTS]</span>), the assistant drafts 2–3 short phrasings
                  grounded only in this company's curated data — relevant business, description,
                  research notes, platforms. It flags when there isn't enough source data rather than
                  guessing. Click a suggestion to drop it into the draft body in place of its token. Steer it with
                  inline guidance like <span class="font-mono">[PRODUCTS - focus on landing gear]</span>.
                </div>
              </span>
            </div>
            <button
              type="button"
              @click="generateSuggestions"
              :disabled="suggesting"
              class="text-xs px-2 py-1 border border-gray-300 rounded-md bg-white hover:bg-gray-100 disabled:opacity-40"
            >{{ suggesting ? 'Generating…' : 'Generate' }}</button>
          </div>
          <div v-if="suggested && suggestions.length === 0" class="text-xs text-gray-400">
            No interpretation tokens found in the draft.
          </div>
          <ul v-else class="space-y-3">
            <li v-for="(s, i) in suggestions" :key="i">
              <div class="text-xs font-medium text-gray-600">{{ s.token }}</div>
              <div v-if="s.insufficient" class="text-xs text-amber-600">Not enough source data to suggest.</div>
              <template v-else>
                <button
                  v-for="(c, ci) in s.candidates"
                  :key="ci"
                  type="button"
                  title="Click to insert in place of the token"
                  @click="insertSuggestion(s, c)"
                  class="block text-left w-full rounded px-1 py-0.5 hover:bg-gray-200 cursor-pointer"
                >{{ c }}</button>
                <div v-if="s.provenance && s.provenance.length" class="text-[11px] text-gray-400 mt-0.5">
                  from: {{ s.provenance.join(', ') }}
                </div>
              </template>
            </li>
          </ul>
        </div>
        <h2 class="font-semibold text-gray-700 mb-2">Reference</h2>
        <dl class="space-y-3">
          <div>
            <dt class="text-xs text-gray-400">Company</dt>
            <dd>{{ panel.company_name || '—' }}</dd>
          </div>
          <div v-if="panel.dba">
            <dt class="text-xs text-gray-400">DBA</dt>
            <dd>{{ panel.dba }}</dd>
          </div>
          <div v-if="panel.website_url">
            <dt class="text-xs text-gray-400">Website</dt>
            <dd>
              <a :href="'https://' + panel.website_url" target="_blank" rel="noopener" class="text-blue-600 hover:underline break-all">{{ panel.website_url }}</a>
            </dd>
          </div>

          <!-- CRM history, grouped by campaign. The company's current campaign
               sits first and starts open; earlier campaigns start collapsed. -->
          <div v-if="crmCampaigns.length" class="border-t border-gray-200 pt-2">
            <div class="text-xs text-gray-400 mb-1">CRM details</div>
            <div class="space-y-1">
              <div v-for="group in crmCampaigns" :key="group.campaign_id" class="border border-gray-200 rounded-md bg-white">
                <button
                  type="button"
                  @click="toggleCampaign(group)"
                  class="flex items-start gap-1 w-full text-left p-2"
                >
                  <ChevronDownIcon v-if="isCampaignOpen(group)" class="w-4 h-4 shrink-0 text-gray-400 mt-0.5" />
                  <ChevronRightIcon v-else class="w-4 h-4 shrink-0 text-gray-400 mt-0.5" />
                  <span class="min-w-0 flex-1">
                    <span class="block text-xs font-medium text-gray-700 break-words">
                      {{ group.campaign_name || '—' }}
                      <span v-if="group.is_current" class="text-[10px] text-gray-400">· current</span>
                    </span>
                    <span
                      v-if="group.crm_status || group.detail_count"
                      class="inline-block rounded bg-yellow-100 px-1 text-[11px] text-gray-600"
                    >
                      <span v-if="group.crm_status">{{ group.crm_status }}</span>
                      <span v-if="group.crm_status && group.detail_count"> · </span>
                      <span v-if="group.detail_count">{{ group.detail_count }} detail{{ group.detail_count === 1 ? '' : 's' }}</span>
                    </span>
                  </span>
                </button>
                <ul v-if="isCampaignOpen(group)" class="px-2 pb-2 space-y-1">
                  <li v-for="detail in visibleDetails(group)" :key="detail.id" class="border-t border-gray-100 pt-1">
                    <div class="flex items-baseline justify-between gap-2">
                      <span class="text-xs text-gray-700 truncate">{{ detail.action_type || '—' }}</span>
                      <span class="text-[11px] text-gray-400 whitespace-nowrap">{{ detailDateLabel(detail) }}</span>
                    </div>
                    <div class="text-[11px] text-gray-500">{{ detail.user || '—' }}</div>
                    <!-- Notes are one line here; the full text shows on hover. -->
                    <div v-if="detail.notes" class="relative group">
                      <div class="text-[11px] text-gray-600 truncate cursor-help">{{ detail.notes }}</div>
                      <div class="pointer-events-none absolute left-0 top-5 z-30 hidden group-hover:block w-72 max-h-64 overflow-hidden p-3 rounded-md bg-gray-800 text-white text-xs font-normal whitespace-pre-wrap leading-relaxed shadow-lg">
                        {{ detail.notes }}
                      </div>
                    </div>
                  </li>
                  <li v-if="!group.details.length" class="text-[11px] text-gray-400 pt-1">No details in this campaign.</li>
                  <li v-else-if="group.details.length > DEFAULT_VISIBLE_DETAILS" class="pt-1">
                    <button
                      type="button"
                      @click="toggleAllDetails(group)"
                      class="text-[11px] text-blue-600 hover:underline"
                    >
                      {{ areAllDetailsShown(group) ? 'Show fewer' : `Show all ${group.details.length}` }}
                    </button>
                  </li>
                </ul>
              </div>
            </div>
          </div>

          <div class="border-t border-gray-200 pt-2">
            <button type="button" @click="showResearch = !showResearch" class="flex items-center gap-1 w-full text-left">
              <ChevronDownIcon v-if="showResearch" class="w-4 h-4 text-gray-400" />
              <ChevronRightIcon v-else class="w-4 h-4 text-gray-400" />
              <span class="text-xs text-gray-400">Research notes</span>
            </button>
            <dd v-if="showResearch" class="whitespace-pre-wrap text-gray-700 mt-1">{{ panel.account_manager_research_notes || '—' }}</dd>
          </div>

          <div class="border-t border-gray-200 pt-2">
            <button type="button" @click="showContacts = !showContacts" class="flex items-center gap-1 w-full text-left">
              <ChevronDownIcon v-if="showContacts" class="w-4 h-4 text-gray-400" />
              <ChevronRightIcon v-else class="w-4 h-4 text-gray-400" />
              <span class="text-xs text-gray-400">Primary contacts</span>
            </button>
            <template v-if="showContacts">
              <dd v-if="panel.primary_contacts && panel.primary_contacts.length" class="mt-1">
                <ul class="space-y-2">
                  <li v-for="(c, i) in panel.primary_contacts" :key="i" class="border border-gray-200 rounded-md bg-white p-2">
                    <div>
                      <button v-if="c.full_name" type="button" title="Click to copy" @click="copy(c.full_name)" class="rounded px-0.5 hover:bg-gray-200 cursor-pointer">{{ c.full_name }}</button>
                      <span v-else>—</span>
                    </div>
                    <div v-if="c.title" class="text-xs text-gray-500 flex">
                      Title:
                      <span type="button" title="Click to copy" @click="copy(c.title)" class="rounded px-0.5 hover:bg-gray-200 cursor-pointer">{{ c.title }}</span>
                    </div>
                    <div v-if="c.preferred_name" class="text-xs text-gray-500">
                      Preferred:
                      <button type="button" title="Click to copy" @click="copy(c.preferred_name)" class="rounded px-0.5 hover:bg-gray-200 cursor-pointer">{{ c.preferred_name }}</button>
                    </div>
                    <div v-if="c.business_email" class="text-xs text-gray-500">
                      Business:
                      <!-- break-all: a long address has no break opportunity of
                           its own and would otherwise run past the card. -->
                      <button type="button" title="Click to copy" @click="copy(c.business_email)" class="max-w-full break-all text-left rounded px-0.5 hover:bg-gray-200 cursor-pointer">{{ c.business_email }}</button>
                    </div>
                    <div v-if="c.personal_email" class="text-xs text-gray-500">
                      Personal:
                      <button type="button" title="Click to copy" @click="copy(c.personal_email)" class="max-w-full break-all text-left rounded px-0.5 hover:bg-gray-200 cursor-pointer">{{ c.personal_email }}</button>
                    </div>
                  </li>
                </ul>
              </dd>
              <dd v-else class="text-gray-400 mt-1">—</dd>
            </template>
          </div>

          <div class="border-t border-gray-200 pt-2">
            <button type="button" @click="showDescription = !showDescription" class="flex items-center gap-1 w-full text-left">
              <ChevronDownIcon v-if="showDescription" class="w-4 h-4 text-gray-400" />
              <ChevronRightIcon v-else class="w-4 h-4 text-gray-400" />
              <span class="text-xs text-gray-400">Description</span>
            </button>
            <dd v-if="showDescription" class="whitespace-pre-wrap text-gray-700 mt-1">{{ panel.description || '—' }}</dd>
          </div>
        </dl>
      </aside>

      <!-- Editor. Locked (non-interactive) while the draft is scheduled so the
           queued send can't be edited out from under itself. -->
      <div class="col-span-2" :class="{ 'pointer-events-none opacity-60': isLocked }">
        <div
          v-if="draft.in_reply_to"
          class="mb-3 rounded-md border border-indigo-200 bg-indigo-50 text-xs text-indigo-800"
        >
          <div class="flex items-center gap-2 px-3 py-2">
            <span class="font-semibold shrink-0">↩ Follow-up</span>
            <span class="truncate text-indigo-700 min-w-0">
              In reply to “{{ draft.in_reply_to.subject || '(no subject)' }}”<span v-if="draft.in_reply_to.occurred_at || draft.in_reply_to.sent_at"> · {{ formatDate(draft.in_reply_to.occurred_at || draft.in_reply_to.sent_at) }}</span>
            </span>
            <!-- Shown in place rather than linked: the email being answered is
                 what you read while writing the answer. -->
            <button
              v-if="draft.in_reply_to.body_html"
              type="button"
              @click="toggleParent"
              class="ml-auto shrink-0 px-2 py-0.5 rounded-md border border-indigo-300 bg-white text-indigo-700 hover:bg-indigo-100"
            >{{ showParent ? 'Hide email' : 'View email' }}</button>
            <button
              v-if="parentThreadMessage && parentThreadMessage.can_open_in_outlook"
              type="button"
              @click="onOpenInOutlook(parentThreadMessage.id)"
              class="shrink-0 px-2 py-0.5 rounded-md border border-indigo-300 bg-white text-indigo-700 hover:bg-indigo-100"
              :class="{ 'ml-auto': !draft.in_reply_to.body_html }"
            >Open in Outlook</button>
          </div>
          <div v-if="showParent" class="border-t border-indigo-200 bg-white rounded-b-md px-3 py-2 text-gray-700">
            <dl class="leading-5 mb-2">
              <div class="flex gap-2">
                <dt class="text-gray-400 w-10 shrink-0">From:</dt>
                <dd class="truncate">
                  <span v-if="draft.in_reply_to.from_name" class="font-medium">{{ draft.in_reply_to.from_name }}</span>
                  <span :class="draft.in_reply_to.from_name ? 'text-gray-500' : 'font-medium'">
                    {{ draft.in_reply_to.from_name ? `<${draft.in_reply_to.from_email}>` : (draft.in_reply_to.from_email || 'Unknown sender') }}
                  </span>
                </dd>
              </div>
              <div v-if="draft.in_reply_to.to_email" class="flex gap-2">
                <dt class="text-gray-400 w-10 shrink-0">To:</dt>
                <dd class="truncate">{{ draft.in_reply_to.to_email }}</dd>
              </div>
            </dl>
            <!-- Sanitized server-side: arrivals at ingest, and our own sends are
                 our own template HTML. -->
            <div class="text-sm max-h-96 overflow-y-auto parent-email-body" style="color: #000000" v-html="renderEmailBody(draft.in_reply_to.body_html, draft.in_reply_to.inline_images)"></div>
            <div v-if="parentThreadMessage && parentThreadMessage.has_attachments" class="mt-2 flex items-center gap-1 text-xs text-gray-500">
              <PaperClipIcon class="w-3.5 h-3.5 text-gray-400" />
              Has attachments, open in Outlook to view
            </div>

            <!-- What came before it, one email per click, newest first. Read off
                 the thread in time order rather than by following reply links,
                 which break at the first inbound email. -->
            <div v-for="m in shownEarlier" :key="m.id" class="border-t border-gray-200 mt-3 pt-2">
              <!-- Hiding collapses to this line rather than dropping the email,
                   so it keeps its place and earlier ones stay reachable. -->
              <div class="flex items-center justify-between gap-3" :class="collapsedEarlier[m.id] ? '' : 'mb-1'">
                <div class="flex items-center gap-2 min-w-0">
                  <span
                    class="text-[10px] px-1.5 py-0.5 rounded-full uppercase tracking-wide shrink-0"
                    :class="kindMeta(m.kind).badgeClass"
                  >{{ kindMeta(m.kind).label }}</span>
                  <span v-if="collapsedEarlier[m.id]" class="truncate text-gray-600">{{ m.from_name || m.from_email || 'Unknown sender' }}</span>
                </div>
                <div class="flex items-center gap-2 shrink-0">
                  <span class="text-gray-500 whitespace-nowrap">{{ formatDate(m.received_at) }}</span>
                  <button
                    v-if="m.can_open_in_outlook"
                    type="button"
                    @click="onOpenInOutlook(m.id)"
                    class="px-2 py-0.5 rounded-md border border-gray-300 bg-white text-gray-700 hover:bg-gray-100"
                  >Open in Outlook</button>
                  <button
                    type="button"
                    @click="collapsedEarlier[m.id] = !collapsedEarlier[m.id]"
                    class="px-2 py-0.5 rounded-md border border-gray-300 bg-white text-gray-700 hover:bg-gray-100"
                  >{{ collapsedEarlier[m.id] ? 'Show' : 'Hide' }}</button>
                </div>
              </div>
              <template v-if="!collapsedEarlier[m.id]">
              <dl class="leading-5 mb-2">
                <div class="flex gap-2">
                  <dt class="text-gray-400 w-10 shrink-0">From:</dt>
                  <dd class="truncate">
                    <span v-if="m.from_name" class="font-medium">{{ m.from_name }}</span>
                    <span :class="m.from_name ? 'text-gray-500' : 'font-medium'">
                      {{ m.from_name ? `<${m.from_email}>` : (m.from_email || 'Unknown sender') }}
                    </span>
                  </dd>
                </div>
                <div v-if="m.to_email" class="flex gap-2">
                  <dt class="text-gray-400 w-10 shrink-0">To:</dt>
                  <dd class="truncate">{{ m.to_email }}</dd>
                </div>
              </dl>
              <div class="text-sm max-h-96 overflow-y-auto parent-email-body" style="color: #000000" v-html="renderEmailBody(m.body_html, m.inline_images)"></div>
              <div v-if="m.has_attachments" class="mt-2 flex items-center gap-1 text-xs text-gray-500">
                <PaperClipIcon class="w-3.5 h-3.5 text-gray-400" />
                Has attachments, open in Outlook to view
              </div>
              </template>
            </div>

            <button
              v-if="revealedEarlier < earlierMessages.length"
              type="button"
              @click="revealedEarlier += 1"
              class="mt-3 px-2 py-0.5 rounded-md border border-gray-300 bg-white text-gray-700 hover:bg-gray-100"
            >View earlier email ({{ earlierMessages.length - revealedEarlier }} more)</button>
          </div>
        </div>
        <div class="mb-2">
          <label class="block text-xs text-gray-500 mb-1">From</label>
          <!-- A reply can go from any mailbox that received the email being
               answered and that you can send from (Graph only replies from
               there). Picked automatically; the picker shows when there's a
               choice. The signature follows the mailbox. -->
          <select
            v-if="fromOptions.length > 1"
            v-model="sendMailboxId"
            @change="onFromChange"
            :disabled="isLocked"
            class="w-full border rounded-md p-2 text-sm bg-white"
          >
            <option v-for="m in fromOptions" :key="m.id" :value="m.id">
              {{ m.display_name ? `${m.display_name} <${m.email_address}>` : m.email_address }}
            </option>
          </select>
          <div v-else class="text-sm text-gray-700">{{ draft.from_email || '—' }}</div>
        </div>
        <div class="mb-2 grid grid-cols-2 gap-3">
          <div>
            <label class="block text-xs text-gray-500 mb-1">To</label>
            <input
              v-model="recipient"
              @input="scheduleSave"
              type="email"
              placeholder="recipient@company.com"
              class="w-full border rounded-md p-2 text-sm"
            />
            <select
              v-if="recipientOptions.length"
              @change="onPickRecipient"
              class="w-full border rounded-md p-2 text-sm mt-1 bg-white"
            >
              <option value="">Pick a contact email…</option>
              <option v-for="(o, i) in recipientOptions" :key="i" :value="i">{{ o.label }} — {{ o.email }}</option>
            </select>
            <p v-if="!recipient" class="text-xs text-red-500 mt-1">No recipient — add an address to send.</p>
          </div>
          <div>
            <div class="flex items-center gap-1 mb-1">
              <label class="block text-xs text-gray-500">Contact name</label>
              <span class="relative group flex items-center">
                <InformationCircleIcon class="w-4 h-4 text-gray-400 cursor-help" />
                <div class="pointer-events-none absolute left-0 top-6 z-30 hidden group-hover:block w-72 p-3 rounded-md bg-gray-800 text-white text-xs font-normal leading-relaxed shadow-lg">
                  Who this is addressed to. Recorded on the CRM detail when sent.
                </div>
              </span>
            </div>
            <input
              v-model="contactName"
              @input="scheduleSave"
              type="text"
              placeholder="Jane Doe"
              class="w-full border rounded-md p-2 text-sm"
            />
          </div>
        </div>

        <!-- Cc / Bcc. Collapsed until used, so the common case (one recipient)
             stays a two-field form. -->
        <div class="mb-2">
          <button
            v-if="!showCcBcc"
            type="button"
            @click="showCcBcc = true"
            class="text-xs text-indigo-600 hover:underline"
          >+ Cc / Bcc</button>

          <div v-else class="grid grid-cols-2 gap-3">
            <div v-for="row in CC_ROWS" :key="row.field">
              <label class="block text-xs text-gray-500 mb-1">{{ row.label }}</label>
              <div
                class="w-full border rounded-md p-1 text-sm flex flex-wrap gap-1 items-center bg-white"
                :class="{ 'opacity-60 pointer-events-none': isLocked }"
              >
                <span
                  v-for="(address, i) in addressList(row.field)"
                  :key="address"
                  class="flex items-center gap-1 bg-gray-100 border border-gray-300 rounded px-1.5 py-0.5 text-xs"
                >
                  {{ address }}
                  <button
                    type="button"
                    class="text-gray-400 hover:text-red-600 font-semibold"
                    @click="removeAddress(row.field, i)"
                  >×</button>
                </span>
                <input
                  v-model="addressDraft[row.field]"
                  @keydown.enter.prevent="commitAddress(row.field)"
                  @keydown.,.prevent="commitAddress(row.field)"
                  @keydown.delete="backspaceAddress(row.field)"
                  @blur="commitAddress(row.field)"
                  type="text"
                  :placeholder="addressList(row.field).length ? '' : 'name@company.com'"
                  class="flex-1 min-w-[8rem] p-1 text-sm outline-none"
                />
              </div>
            </div>
          </div>

          <p v-if="showCcBcc && draft.template_bcc" class="text-xs text-gray-400 mt-1">
            This template also bccs <span class="font-mono">{{ draft.template_bcc }}</span> on every send.
          </p>
        </div>
        <div class="mb-2">
          <label class="block text-xs text-gray-500 mb-1">Subject</label>
          <!-- A follow-up sends as a reply in the parent's thread, and the mail
               server derives its subject from that thread ("RE: <original>").
               Editing here would change nothing that goes out, so it's shown
               read-only rather than offered as a field. -->
          <div v-if="draft.in_reply_to" class="text-sm text-gray-700">
            {{ subject || '—' }}
            <span class="text-xs text-gray-400">· set by the thread this replies to</span>
          </div>
          <template v-else>
            <input v-model="subject" @input="scheduleSave" class="w-full border rounded-md p-2 text-sm" />
            <p v-if="subjectMissing" class="text-xs text-red-500 mt-1">No subject — add one to send.</p>
          </template>
        </div>
        <!-- Attachments as Outlook shows them: a row of file cards between the
             subject and the body, led by the Attach button. -->
        <div
          v-if="attachments.length || uploadingAttachment || (!isLocked && draft.status !== 'sent')"
          class="mb-2 flex flex-wrap items-center gap-2"
        >
          <label
            v-if="!isLocked && draft.status !== 'sent'"
            class="inline-flex items-center gap-1 text-xs px-2 py-1 border border-gray-300 rounded-md cursor-pointer bg-white hover:bg-gray-50"
            :class="{ 'opacity-50 pointer-events-none': uploadingAttachment }"
          >
            <PaperClipIcon class="w-3.5 h-3.5 text-gray-500" />
            Attach
            <input type="file" class="hidden" @change="uploadAttachment" />
          </label>
          <div
            v-for="attachment in attachments"
            :key="attachment.key"
            class="group flex items-center gap-2 w-56 pl-2 pr-1 py-1.5 border border-gray-300 rounded-md bg-white hover:bg-gray-50"
          >
            <PaperClipIcon class="w-4 h-4 shrink-0 text-gray-400" />
            <a :href="attachment.url" target="_blank" rel="noopener" class="min-w-0 flex-1" :title="attachment.filename">
              <div class="text-xs text-gray-800 truncate">{{ attachment.filename }}</div>
              <div class="text-[11px] text-gray-400">
                {{ formatSize(attachment.size) }}<template v-if="!attachment.removable"> · from template</template>
              </div>
            </a>
            <!-- Template files belong to the template, so removing one here
                 would change every draft built from it. -->
            <button
              v-if="attachment.removable && !isLocked && draft.status !== 'sent'"
              type="button"
              class="shrink-0 p-0.5 rounded text-gray-400 hover:text-red-600 hover:bg-gray-100"
              title="Remove attachment"
              @click="removeAttachment(attachment)"
            ><XMarkIcon class="w-4 h-4" /></button>
          </div>
          <div
            v-if="uploadingAttachment"
            class="flex items-center gap-2 w-56 px-2 py-1.5 border border-dashed border-gray-300 rounded-md text-xs text-gray-400"
          >
            <PaperClipIcon class="w-4 h-4 shrink-0" />
            Uploading…
          </div>
        </div>
        <div v-if="showTokenWarnings && (hasCurlyBraces || hasSquareBrackets)" class="mb-2 space-y-1">
          <div
            v-if="hasCurlyBraces"
            class="flex items-start gap-2 rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800"
          >
            <ExclamationTriangleIcon class="w-4 h-4 shrink-0 mt-0.5 text-amber-500" />
            <span>
              This draft contains a <span class="font-mono font-semibold">{</span> or <span class="font-mono font-semibold">}</span> —
              often an injectable field like <span class="font-mono">{{ exampleInjectableField }}</span> that didn't get filled in. Double-check before sending.
            </span>
          </div>
          <div
            v-if="hasSquareBrackets"
            class="flex items-start gap-2 rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800"
          >
            <ExclamationTriangleIcon class="w-4 h-4 shrink-0 mt-0.5 text-amber-500" />
            <span>
              This draft contains a <span class="font-mono font-semibold">[</span> or <span class="font-mono font-semibold">]</span> —
              often an AI placeholder like <span class="font-mono">[PRODUCTS]</span> that was never replaced. Double-check before sending.
            </span>
          </div>
        </div>
        <RichTextEditor ref="richEditor" :model-value="body" @update:model-value="onBodyUpdate" />

        <!-- The mailbox signature, appended on send. Shown here rather than
             merged into the body so it stays removable in one click -- most
             templates already end in a typed "Regards, <name>", and two
             sign-offs is worse than none. -->
        <div v-if="draft.signature_preview" class="mt-2">
          <div class="flex items-center justify-between mb-1">
            <label class="block text-xs text-gray-500">Signature</label>
            <button
              v-if="!isLocked && draft.status !== 'sent'"
              type="button"
              class="text-xs"
              :class="includeSignature ? 'text-gray-400 hover:text-red-600' : 'text-indigo-600 hover:underline'"
              @click="toggleSignature"
            >{{ includeSignature ? 'Remove' : 'Add back' }}</button>
          </div>
          <!-- The signature would sign an email that's already signed: a bounced
               send being sent again (its body is what went out, signature and
               all), or a template ending in a typed sign-off. -->
          <div
            v-if="signatureConflict"
            class="mb-1 flex items-start gap-2 rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800"
          >
            <ExclamationTriangleIcon class="w-4 h-4 shrink-0 mt-0.5 text-amber-500" />
            <span class="flex-1">
              <template v-if="signatureConflict.kind === 'signature'">
                The body already contains this mailbox's signature. With the signature on, it will appear twice.
              </template>
              <template v-else>
                The body already ends with a sign-off (<span class="font-medium">“{{ signatureConflict.text }}”</span>). With the signature on, the email will be signed twice.
              </template>
            </span>
            <button
              type="button"
              @click="toggleSignature"
              class="shrink-0 px-2 py-0.5 rounded-md border border-amber-300 bg-white text-amber-800 hover:bg-amber-100"
            >Remove signature</button>
          </div>
          <div
            v-if="includeSignature"
            class="border border-gray-200 rounded px-2 py-1 bg-gray-50 text-sm"
            v-html="draft.signature_preview"
          ></div>
          <p v-else class="text-xs text-gray-400">
            Not included — this email sends without your mailbox signature.
          </p>
        </div>
      </div>

      <!-- CRM actions. Lifted out of the editor column: these are the only
           controls on the page that write outside the draft, onto the company
           itself, so they sit opposite the context they act on. -->
      <!-- Hovering Send nudges this pane: these controls write to the COMPANY,
           not the draft, and the connection is easy to miss from across the
           page. A one-shot nudge plus a held highlight, not a repeating shake --
           a shake reads as an error, and this is not a warning. -->
      <aside
        class="col-span-1 rounded-md border p-3 text-sm transition-shadow duration-200"
        :class="[
          isBounced ? 'border-amber-300 bg-amber-50' : 'border-indigo-100 bg-indigo-50/40',
          sendHover ? 'crm-armed' : '',
          readOnly ? 'pointer-events-none opacity-60' : '',
        ]"
      >
        <div v-if="isBounced" class="flex items-start gap-2 mb-3 px-1 text-xs text-amber-800">
          <ExclamationTriangleIcon class="w-4 h-4 shrink-0 mt-0.5 text-amber-500" />
          <span>
            This email <span class="font-semibold">bounced</span> — it never reached the recipient. The CRM status and next action below were applied when the draft was sent, so they may no longer be accurate. Review and update the company's CRM state if needed.
          </span>
        </div>
        <div v-else class="flex items-center gap-2 mb-3">
          <BuildingOffice2Icon class="w-4 h-4 shrink-0 text-indigo-400" />
          <div class="text-xs font-semibold text-indigo-800">Applied to the company on send</div>
        </div>
        <div class="mb-2 grid grid-cols-1 gap-3">
          <div>
            <div class="flex items-center gap-1 mb-1">
              <label class="block text-xs font-medium text-gray-700">CRM status</label>
              <span class="relative group flex items-center">
                <InformationCircleIcon class="w-4 h-4 text-gray-400 cursor-help" />
                <div class="pointer-events-none absolute left-0 top-6 z-30 hidden group-hover:block w-72 p-3 rounded-md bg-gray-800 text-white text-xs font-normal leading-relaxed shadow-lg">
                  Applied to this company for its campaign when the email sends. Skipped if it's already the current status.
                </div>
              </span>
            </div>
            <select
              v-model="crmStatusId"
              @change="scheduleSave"
              class="w-full border rounded-md p-2 text-sm bg-white"
            >
              <option :value="null">— Don't change CRM status —</option>
              <option v-for="s in crmStatusOptions" :key="s.id" :value="s.id">{{ s.status_name }}</option>
            </select>
          </div>
          <div>
            <div class="flex items-center gap-1 mb-1">
              <label class="block text-xs font-medium text-gray-700">CRM detail</label>
              <span class="relative group flex items-center">
                <InformationCircleIcon class="w-4 h-4 text-gray-400 cursor-help" />
                <div class="pointer-events-none absolute left-0 top-6 z-30 hidden group-hover:block w-72 p-3 rounded-md bg-gray-800 text-white text-xs font-normal leading-relaxed shadow-lg">
                  Logged against this company for its campaign when the email sends.
                  Starts as the template's detail — change it if this send is really a
                  different step, or clear it to log nothing.
                </div>
              </span>
            </div>
            <select
              v-model="crmDetailId"
              @change="scheduleSave"
              class="w-full border rounded-md p-2 text-sm bg-white"
            >
              <option :value="null">— Don't log a CRM detail —</option>
              <option v-for="a in crmDetailOptions" :key="a.id" :value="a.id">{{ a.action_name }}</option>
            </select>
            <div v-if="templateCrmDetail && !usingTemplateCrmDetail" class="mt-1 text-xs text-gray-500">
              Template default: {{ templateCrmDetail.action_name }}
              <button
                type="button"
                @click="resetCrmDetailToTemplate"
                class="ml-1 underline hover:text-gray-700"
              >reset</button>
            </div>
            <div v-if="crmDetailId" class="mt-2">
              <div class="flex items-center gap-1 mb-1">
                <label for="crm-detail-notes" class="block text-xs font-medium text-gray-700">CRM detail notes</label>
                <span class="relative group flex items-center">
                  <InformationCircleIcon class="w-4 h-4 text-gray-400 cursor-help" />
                  <div class="pointer-events-none absolute left-0 top-6 z-30 hidden group-hover:block w-72 p-3 rounded-md bg-gray-800 text-white text-xs font-normal leading-relaxed shadow-lg">
                    Saved with the CRM detail when the email sends. Leave it blank to log
                    the note shown in grey.
                  </div>
                </span>
              </div>
              <textarea
                id="crm-detail-notes"
                v-model="crmDetailNotes"
                @input="scheduleSave"
                rows="2"
                :placeholder="defaultCrmDetailNote"
                class="w-full border rounded-md p-2 text-sm bg-white"
              ></textarea>
            </div>
          </div>
        </div>
        <div class="mb-2 grid grid-cols-1 gap-3">
          <div>
            <div class="flex items-center gap-1 mb-1">
              <label class="block text-xs font-medium text-gray-700">CRM next action</label>
              <span class="relative group flex items-center">
                <InformationCircleIcon class="w-4 h-4 text-gray-400 cursor-help" />
                <div class="pointer-events-none absolute left-0 top-6 z-30 hidden group-hover:block w-72 p-3 rounded-md bg-gray-800 text-white text-xs font-normal leading-relaxed shadow-lg">
                  Written onto the company, with the due date, when the email sends.
                  Your choice carries over to the next draft you open until you change it.
                </div>
              </span>
            </div>
            <select
              v-model="crmNextAction"
              @change="onCrmNextActionChange"
              class="w-full border rounded-md p-2 text-sm bg-white"
            >
              <option :value="null">— Don't change next action —</option>
              <option v-for="o in crmNextActionOptions" :key="o.value" :value="o.value">{{ o.label }}</option>
            </select>
          </div>
          <div>
            <div class="flex items-center gap-1 mb-1">
              <label class="block text-xs font-medium text-gray-700">Next action due date</label>
              <span class="relative group flex items-center">
                <InformationCircleIcon class="w-4 h-4 text-gray-400 cursor-help" />
                <div class="pointer-events-none absolute left-0 top-6 z-30 hidden group-hover:block w-72 p-3 rounded-md bg-gray-800 text-white text-xs font-normal leading-relaxed shadow-lg">
                  Due date for the next action above. Only applied when a next action is set.
                  Also carries over to the next draft you open.
                </div>
              </span>
            </div>
            <input
              v-model="crmNextActionDueDate"
              @change="onCrmNextActionChange"
              type="date"
              class="w-full border rounded-md p-2 text-sm"
            />
            <!-- Shortcuts sit under the picker rather than beside it: the column
                 is a quarter of the page, and a date input plus two buttons on
                 one row leaves none of them usable. -->
            <div class="flex items-center gap-2 mt-1">
              <button
                type="button"
                @click="setDueDateInDays(7)"
                class="px-2 py-1 rounded-md text-xs border border-gray-300 bg-white hover:bg-gray-100"
              >+1 week</button>
              <button
                type="button"
                @click="setDueDateInDays(14)"
                class="px-2 py-1 rounded-md text-xs border border-gray-300 bg-white hover:bg-gray-100"
              >+2 weeks</button>
            </div>
          </div>
        </div>
      </aside>
    </div>
    </div>

    <FollowUpTemplateModal
      :open="showFollowUpModal"
      :count="1"
      :submitting="followingUp"
      @confirm="onConfirmFollowUp"
      @cancel="showFollowUpModal = false"
    />

    <ScheduleSendModal
      :open="showScheduleModal"
      :submitting="scheduling"
      @confirm="onConfirmSchedule"
      @cancel="showScheduleModal = false"
    />
  </div>
  <div v-else class="p-8 text-center text-gray-500">Loading…</div>
</template>

<script setup>
import { ref, reactive, computed, onMounted, onBeforeUnmount, watch, nextTick } from 'vue'
import { useStore } from 'vuex'
import { useRoute, useRouter } from 'vue-router'
import { useToast } from 'vue-toast-notification'
import RichTextEditor from '@/components/base-components/RichTextEditor.vue'
import ScheduleSendModal from '@/components/outreach-drafts/ScheduleSendModal.vue'
import FollowUpTemplateModal from '@/components/outreach-drafts/FollowUpTemplateModal.vue'
import { InformationCircleIcon, ChevronDownIcon, ChevronRightIcon, ExclamationTriangleIcon, BuildingOffice2Icon, PaperClipIcon, XMarkIcon } from '@heroicons/vue/20/solid'
import {
  getOutreachDraft,
  updateOutreachDraft,
  sendOutreachDraft,
  scheduleOutreachDraft,
  unscheduleOutreachDraft,
  discardOutreachDraft,
  recreateOutreachDraft,
  prepareFollowUpOutreachDraft,
  replyToOutreachDraft,
  generateOutreachSuggestions,
  addDraftAttachment,
  removeDraftAttachment,
} from '@/services/OutreachDraft'
import { getCrmStatuses } from '@/services/CrmStatus'
import { getActions } from '@/services/CrmAction'
import { CRM_NEXT_ACTION_OPTIONS } from '@/utils/crmNextActionOptions'
import { kindMeta } from '@/utils/outreachResponseKind'
import { renderEmailBody } from '@/utils/emailHtml'
import { openInOutlook } from '@/utils/openInOutlook'
// Local formatDate below renders send timestamps; the composable's is for the
// date-only CRM detail fields (UTC-safe, so a plain YYYY-MM-DD doesn't shift).
import { getDateAsString, formatDate as formatDateOnly } from '@/composables/formatDate'

const store = useStore()
const route = useRoute()
const router = useRouter()
const $toast = useToast()

const draft = ref(null)
// Whether the "In reply to" banner has the email being answered expanded, and
// how many of the thread's earlier emails have been revealed beneath it.
const showParent = ref(false)
const revealedEarlier = ref(0)
// The thread arrives newest first; everything after the parent is older than
// it. A parent missing from the thread (no timestamp) has nothing to show below.
const earlierMessages = computed(() => {
  const messages = (draft.value && draft.value.thread_messages) || []
  const parentId = draft.value && draft.value.in_reply_to && draft.value.in_reply_to.id
  const i = messages.findIndex((m) => m.id === parentId)
  return i >= 0 ? messages.slice(i + 1) : []
})
const shownEarlier = computed(() => earlierMessages.value.slice(0, revealedEarlier.value))
// The parent's own entry on the thread: in_reply_to is a light summary without
// the per-viewer flags (attachments, Open in Outlook).
const parentThreadMessage = computed(() => {
  const messages = (draft.value && draft.value.thread_messages) || []
  const parentId = draft.value && draft.value.in_reply_to && draft.value.in_reply_to.id
  return messages.find((m) => m.id === parentId) || null
})

async function onOpenInOutlook(messageId) {
  const error = await openInOutlook(messageId, store.state.auth.token)
  if (error) $toast.error(error)
}
// Hiding folds the whole chain back up, so reopening starts from the parent.
// Earlier emails the analyst has hidden again, by id.
const collapsedEarlier = reactive({})
function toggleParent() {
  showParent.value = !showParent.value
  if (!showParent.value) {
    revealedEarlier.value = 0
    Object.keys(collapsedEarlier).forEach((id) => { delete collapsedEarlier[id] })
  }
}
const richEditor = ref(null)
const subject = ref('')
const body = ref('')
const recipient = ref('')
const contactName = ref('')
// null = "don't change CRM status". The backend seeds the default (No Response)
// when drafts are prepared, so a null here is a deliberate opt-out, not an
// undecided field -- which is why nothing re-defaults it on load.
const crmStatusId = ref(null)
const crmStatuses = ref([])
// The CRM detail (action type) to log on send. Seeded server-side from the
// template when the draft is prepared, so -- like the CRM status -- a null here
// is the analyst opting out, not an undecided field, and nothing re-defaults it
// on load. The template's own detail stays available as a reset.
const crmDetailId = ref(null)
// A reply's From: one of reply_from_options, sent with every save once there
// is a choice to make.
const sendMailboxId = ref(null)
const fromOptions = computed(() => (draft.value && draft.value.reply_from_options) || [])
// Notes on that CRM detail. Blank logs the generated note, shown as the
// placeholder so the analyst sees what goes in if they leave it.
const crmDetailNotes = ref('')
const crmActionTypes = ref([])
// Next action + due date to write onto the company on send. Unlike the CRM
// status these are not seeded server-side; they carry over from the last draft
// the analyst set them on (see NEXT_ACTION_STICKY_KEYS).
const crmNextAction = ref(null)
const crmNextActionDueDate = ref('')
const crmNextActionOptions = CRM_NEXT_ACTION_OPTIONS
const saving = ref(false)
const sending = ref(false)
const scheduling = ref(false)
const unscheduling = ref(false)
const showScheduleModal = ref(false)
const recreating = ref(false)
const followingUp = ref(false)
const showFollowUpModal = ref(false)
// True while Send or Schedule send is hovered, to flag the CRM pane as part
// of what that button will do.
const sendHover = ref(false)
const suggestions = ref([])
const suggesting = ref(false)
const suggested = ref(false)
let saveTimer = null

const panel = computed(() => (draft.value && draft.value.reference_panel) || {})
const attachments = computed(() => (draft.value && draft.value.attachments) || [])

// Cc / Bcc. Two fields driven by one set of handlers, keyed by field name.
const CC_ROWS = [
  { field: 'cc', label: 'Cc' },
  { field: 'bcc', label: 'Bcc' },
]
const addresses = reactive({ cc: [], bcc: [] })
// The half-typed address in each box, before Enter/comma/blur commits it.
const addressDraft = reactive({ cc: '', bcc: '' })
const showCcBcc = ref(false)
const uploadingAttachment = ref(false)
// Whether the mailbox signature is appended on send. The body is untouched
// either way; this only flips the flag the send path reads.
const includeSignature = ref(true)

function toggleSignature() {
  includeSignature.value = !includeSignature.value
  scheduleSave()
}

// The body as lines of text, one per paragraph or <br>, blank lines dropped.
// textContent alone runs paragraphs together, which would hide a sign-off
// sitting on its own line.
function bodyLines(html) {
  const tmp = document.createElement('div')
  tmp.innerHTML = (html || '')
    .replace(/<br\s*\/?>/gi, '\n')
    .replace(/<\/(p|div|li|h[1-6]|tr)>/gi, '\n')
  return (tmp.textContent || '')
    .split('\n')
    .map((line) => line.replace(/\u00a0/g, ' ').replace(/\s+/g, ' ').trim())
    .filter(Boolean)
}

// Closings that, at the end of an email, mean it is already signed. Longest
// first, so "Best regards" is matched as itself rather than as "Best".
const SIGN_OFFS = [
  'sincerely yours', 'yours sincerely', 'yours truly',
  'warmest regards', 'kind regards', 'warm regards', 'best regards', 'best wishes',
  'all the best', 'thanks again', 'many thanks', 'thank you', 'talk soon',
  'take care', 'respectfully', 'sincerely', 'regards', 'cheers', 'thanks', 'best',
].sort((a, b) => b.length - a.length)

// After the closing: only punctuation ("Regards,") or punctuation plus a name of
// up to three capitalised words ("Best, Patrick"). Anything more is a sentence
// -- "Thanks for your time." -- which is a courtesy, not a signature.
const AFTER_SIGN_OFF = /^[\s,.!\u2013\u2014-]*([A-Z][\w.'-]*(\s+[A-Z][\w.'-]*){0,2})?\s*$/

function trailingSignOff(lines) {
  // The last few lines only: a sign-off ends an email, and "Thanks" mid-body
  // means something else.
  for (const line of lines.slice(-4)) {
    const lower = line.toLowerCase()
    for (const phrase of SIGN_OFFS) {
      if (!lower.startsWith(phrase)) continue
      const rest = line.slice(phrase.length)
      // A whole word: "Bestow" is not "Best".
      if (/^[a-z]/i.test(rest)) continue
      if (AFTER_SIGN_OFF.test(rest)) return line
    }
  }
  return null
}

// Why the mailbox signature would double up on this email, or null. Only while
// it is on and the email is still editable.
const signatureConflict = computed(() => {
  if (!includeSignature.value || !draft.value || !draft.value.signature_preview) return null
  if (draft.value.status === 'sent') return null
  const lines = bodyLines(body.value)
  const signatureText = bodyLines(draft.value.signature_preview).join(' ')
  // The signature's text, or -- for an image-only signature, which has none --
  // the cid: reference the send path writes for its image.
  const hasSignatureText = signatureText.length >= 3 && lines.join(' ').includes(signatureText)
  if (hasSignatureText || /cid:signature(?![-_A-Za-z0-9])/.test(body.value || '')) {
    return { kind: 'signature' }
  }
  const signOff = trailingSignOff(lines)
  return signOff ? { kind: 'sign_off', text: signOff } : null
})

function addressList(field) {
  return addresses[field]
}

// Permissive on purpose: the server validates too, and the send is the real
// arbiter. This only catches a name pasted where an address belongs.
function looksLikeEmail(value) {
  const v = (value || '').trim()
  if (!v || v.includes(' ') || (v.match(/@/g) || []).length !== 1) return false
  const [local, domain] = v.split('@')
  return !!local && domain.includes('.') && !domain.startsWith('.') && !domain.endsWith('.')
}

function commitAddress(field) {
  const value = (addressDraft[field] || '').trim().replace(/,$/, '')
  if (!value) return
  if (!looksLikeEmail(value)) {
    $toast.error(`Not a valid email address: ${value}`)
    return
  }
  if (!addresses[field].some(a => a.toLowerCase() === value.toLowerCase())) {
    addresses[field].push(value)
    scheduleSave()
  }
  addressDraft[field] = ''
}

function removeAddress(field, index) {
  addresses[field].splice(index, 1)
  scheduleSave()
}

// Backspace in an empty box pops the last chip, the way every mail client does.
function backspaceAddress(field) {
  if (addressDraft[field]) return
  if (!addresses[field].length) return
  addresses[field].pop()
  scheduleSave()
}

async function uploadAttachment(event) {
  const file = event.target.files && event.target.files[0]
  // Reset so re-picking the same file after a remove still fires.
  event.target.value = ''
  if (!file) return
  uploadingAttachment.value = true
  try {
    draft.value = await addDraftAttachment(draft.value.id, file, token())
  } catch (e) {
    $toast.error(e?.response?.data?.detail || 'Could not attach that file')
  } finally {
    uploadingAttachment.value = false
  }
}

async function removeAttachment(attachment) {
  try {
    draft.value = await removeDraftAttachment(draft.value.id, attachment.id, token())
  } catch (e) {
    $toast.error(e?.response?.data?.detail || 'Could not remove that attachment')
  }
}
// Server tells us whether a sent draft captured a message id to reply to.
const canFollowUp = computed(() => !!(draft.value && draft.value.can_follow_up))
// A scheduled draft is locked for editing: the queued send would otherwise fire a
// stale version. The analyst unschedules to make changes.
const isScheduled = computed(() => !!(draft.value && draft.value.status === 'scheduled'))
// Seen only through supervisor mode: from a mailbox this user can't send from, so
// the server refuses every change. Opened for reading.
const readOnly = computed(() => !!(draft.value && draft.value.can_edit === false))
const isLocked = computed(() => isScheduled.value || readOnly.value)

// A subject is required on a new outreach email -- a blank one reads as spam and
// there's no thread to fall back on. A follow-up is exempt: it sends as a reply,
// so the mail server derives "RE: <original>" from the parent thread and the
// field here is read-only anyway.
const subjectMissing = computed(() =>
  !!(draft.value && !draft.value.in_reply_to && !(subject.value || '').trim())
)

// Leftover template/AI tokens are usually a mistake in a finished draft: a curly
// brace means an injectable field (e.g. {{primary_contact_first_name}}) didn't get
// substituted; a square bracket means an AI placeholder (e.g. [PRODUCTS]) was never
// filled in. Both can legitimately appear, so these are soft warnings, not blocks.
// We scan the VISIBLE text (subject + body), not the raw HTML, so markup and
// attributes can't trip the check.
function visibleText(htmlOrText) {
  if (!htmlOrText) return ''
  const tmp = document.createElement('div')
  tmp.innerHTML = htmlOrText
  return tmp.textContent || tmp.innerText || ''
}
const draftText = computed(() => `${subject.value || ''}\n${visibleText(body.value)}`)
const hasCurlyBraces = computed(() => /[{}]/.test(draftText.value))
const hasSquareBrackets = computed(() => /[[\]]/.test(draftText.value))
// Only meaningful pre-send; a sent draft is a done deal.
const showTokenWarnings = computed(() => !!(draft.value && draft.value.status !== 'sent'))
// Held as a constant rather than inline in the template: literal "{{ }}" in a
// template breaks Vue's interpolation delimiter matching.
const exampleInjectableField = '{{primary_contact_first_name}}'

// A draft that sent (Graph accepted it) but then bounced is FAILED with the bounce
// marker in send_error. Its on-send CRM side effects (status + next action) were
// applied even though the email never reached anyone, so they may now be stale.
const isBounced = computed(() =>
  !!(draft.value && draft.value.status === 'failed' && /bounce/i.test(draft.value.send_error || ''))
)

// Reference-panel section collapse state: notes + contacts open, description closed.
const showResearch = ref(true)
const showContacts = ref(true)
const showDescription = ref(false)

// CRM details grouped by campaign, current campaign first (server-ordered).
const crmCampaigns = computed(() => panel.value.crm_campaigns || [])
// Only campaigns the analyst has clicked are tracked here; everything else falls
// back to is_current, so the current campaign starts open and the rest closed.
const campaignOpenOverrides = ref({})

function isCampaignOpen(group) {
  const override = campaignOpenOverrides.value[group.campaign_id]
  return override === undefined ? !!group.is_current : override
}

// Long campaign histories are cut to the first few details -- the open ones and
// the most recent resolved -- so a company with years of CRM activity doesn't
// bury the rest of the panel. "Show all" opens the campaign's full list.
const DEFAULT_VISIBLE_DETAILS = 4
const campaignShowAll = ref({})

function areAllDetailsShown(group) {
  return !!campaignShowAll.value[group.campaign_id]
}

function visibleDetails(group) {
  if (areAllDetailsShown(group)) return group.details
  return group.details.slice(0, DEFAULT_VISIBLE_DETAILS)
}

function toggleAllDetails(group) {
  campaignShowAll.value = {
    ...campaignShowAll.value,
    [group.campaign_id]: !areAllDetailsShown(group),
  }
}

function toggleCampaign(group) {
  campaignOpenOverrides.value = {
    ...campaignOpenOverrides.value,
    [group.campaign_id]: !isCampaignOpen(group),
  }
}

// A resolved detail shows its date bare -- in a list of past activity the date
// is self-evidently when it happened. "Due" is the exception worth marking,
// because it points forward. An open detail with no date set still reads "Due",
// just without one.
function detailDateLabel(detail) {
  if (detail.date_type === 'resolved') {
    return detail.date ? formatDateOnly(detail.date) : ''
  }
  return detail.date ? `Due ${formatDateOnly(detail.date)}` : 'Due'
}

function formatSize(bytes) {
  if (bytes === null || bytes === undefined) return ''
  if (bytes < 1024) return `${bytes} B`
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
}

// Human-readable timestamp for the "In reply to … · sent {date}" banner.
function formatDate(iso) {
  if (!iso) return ''
  try { return new Date(iso).toLocaleString() } catch (e) { return iso }
}
// Every CRM status is offered. The picker used to be narrowed to "No Response" on
// the premise that a draft is only ever initial outreach, but follow-ups reply
// within a thread and a send can legitimately move a company anywhere the analyst
// says it moved -- so the choice sits with the person sending rather than with a
// list here. "No Response" is still what a new draft is seeded with server-side,
// so the common case stays a zero-click default.
const crmStatusOptions = computed(() => {
  const offered = crmStatuses.value
  // Keep whatever this draft already carries selectable, so a draft holding a
  // status that has since been removed still displays it rather than rendering
  // as a blank select.
  const current = draft.value && draft.value.crm_status
  if (current && !offered.some((s) => s.id === current.id)) return [...offered, current]
  return offered
})

// Every CRM detail type is offerable here -- unlike the status picker, there's no
// subset that a send can legitimately produce; which step an email represents is
// the analyst's call. Same defensive tail as above: keep whatever the draft
// already carries selectable so it never renders as a blank select.
const crmDetailOptions = computed(() => {
  const offered = crmActionTypes.value
  const current = draft.value && draft.value.crm_detail
  if (current && !offered.some((a) => a.id === current.id)) return [...offered, current]
  return offered
})

// What the draft's template would have used, so the analyst can see what they
// changed away from and put it back.
const templateCrmDetail = computed(() => (draft.value && draft.value.template_crm_detail) || null)
const usingTemplateCrmDetail = computed(() => {
  const fromTemplate = templateCrmDetail.value
  return !!fromTemplate && crmDetailId.value === fromTemplate.id
})

// Mirrors the send's fallback note in outreach_send_service.
const defaultCrmDetailNote = computed(() => {
  const detail = crmDetailOptions.value.find(a => a.id === crmDetailId.value)
  const who = contactName.value || recipient.value
  return detail ? `${detail.action_name} sent to ${who}` : ''
})

function resetCrmDetailToTemplate() {
  if (!templateCrmDetail.value) return
  crmDetailId.value = templateCrmDetail.value.id
  scheduleSave()
}

// The next action + due date carry over between drafts so an analyst working a
// batch sets them once. Kept in localStorage rather than on the server because
// it's a per-analyst working preference, not draft data.
const NEXT_ACTION_STICKY_KEYS = {
  action: 'outreachDraft.crmNextAction',
  dueDate: 'outreachDraft.crmNextActionDueDate',
}

function readStickyNextAction() {
  try {
    return {
      action: window.localStorage.getItem(NEXT_ACTION_STICKY_KEYS.action) || null,
      dueDate: window.localStorage.getItem(NEXT_ACTION_STICKY_KEYS.dueDate) || '',
    }
  } catch (e) {
    // private mode / storage disabled -- fall back to no carry-over
    return { action: null, dueDate: '' }
  }
}

function writeStickyNextAction() {
  try {
    const store_ = window.localStorage
    if (crmNextAction.value) store_.setItem(NEXT_ACTION_STICKY_KEYS.action, crmNextAction.value)
    else store_.removeItem(NEXT_ACTION_STICKY_KEYS.action)
    if (crmNextActionDueDate.value) store_.setItem(NEXT_ACTION_STICKY_KEYS.dueDate, crmNextActionDueDate.value)
    else store_.removeItem(NEXT_ACTION_STICKY_KEYS.dueDate)
  } catch (e) {
    // non-fatal: the values still apply to this draft, they just won't carry over
  }
}

// Editing either control updates what carries over to the next draft -- clearing
// included, since clearing is also a choice.
function onCrmNextActionChange() {
  writeStickyNextAction()
  scheduleSave()
}

function setDueDateInDays(days) {
  const due = new Date()
  due.setDate(due.getDate() + days)
  crmNextActionDueDate.value = getDateAsString(due)
  onCrmNextActionChange()
}

// Both business and personal emails across primary contacts, offered as
// pick-from recipient suggestions.
const recipientOptions = computed(() => {
  const opts = []
  for (const c of (panel.value.primary_contacts || [])) {
    const namePart = [c.full_name, c.title].filter(Boolean).join(' - ')
    const name = c.full_name || ''
    if (c.business_email) opts.push({ email: c.business_email, name, label: `${namePart} (business)`.trim() })
    if (c.personal_email) opts.push({ email: c.personal_email, name, label: `${namePart} (personal)`.trim() })
  }
  return opts
})

// The primary contact whose business/personal email matches an address, if any.
// Used to seed the contact name field for drafts saved before it existed.
function nameForEmail(email) {
  if (!email) return ''
  const target = email.trim().toLowerCase()
  const match = recipientOptions.value.find(o => (o.email || '').trim().toLowerCase() === target)
  return match ? match.name : ''
}

// The primary-contact record that owns an address (business or personal). Lets a
// recipient switch resolve both the contact being replaced and its replacement.
function contactForEmail(email) {
  if (!email) return null
  const target = email.trim().toLowerCase()
  return (panel.value.primary_contacts || []).find(c =>
    (c.business_email || '').trim().toLowerCase() === target ||
    (c.personal_email || '').trim().toLowerCase() === target
  ) || null
}

function firstNameOf(fullName) {
  return (fullName || '').trim().split(/\s+/)[0] || ''
}

function escapeRegExp(s) {
  return s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
}

// Swap one contact's injected values for another's in the draft body. The body is
// rendered with the primary contact's fields substituted in as literal text at
// prepare time (no {{tokens}} survive), so retargeting to a new contact means
// replacing those literals. Best-effort: only values still present are replaced,
// and full name is handled before the bare first name so both greeting styles
// ("Hi Jane Doe," and "Hi Jane,") flip. Returns the list of new values it applied,
// so the caller can flash them in the editor.
function swapContactFieldsInBody(oldContact, newContact) {
  if (!oldContact || !newContact) return []
  const pairs = [
    [oldContact.full_name, newContact.full_name],
    [oldContact.preferred_name, newContact.preferred_name],
    [oldContact.title, newContact.title],
    [firstNameOf(oldContact.full_name), firstNameOf(newContact.full_name)],
  ]
  let htmlBody = body.value || ''
  const applied = []
  for (const [oldRaw, newRaw] of pairs) {
    const from = (oldRaw || '').trim()
    const to = (newRaw || '').trim()
    // Need a real old value to find, a replacement to use, and an actual change.
    if (from.length < 2 || !to || from === to) continue
    // Word-boundary match when the value starts/ends with a word char, so a name
    // isn't caught mid-word (e.g. "Bill" inside "billing").
    const lead = /^\w/.test(from) ? '\\b' : ''
    const trail = /\w$/.test(from) ? '\\b' : ''
    const re = new RegExp(lead + escapeRegExp(from) + trail, 'g')
    if (re.test(htmlBody)) {
      htmlBody = htmlBody.replace(re, to)
      applied.push(to)
    }
  }
  if (applied.length) body.value = htmlBody
  return applied
}

function token() {
  return store.state.auth.token
}

async function load() {
  try {
    // Reset per-draft transient state so a reused component (navigating between
    // draft ids) never shows the previous draft's suggestions.
    suggestions.value = []
    suggested.value = false
    const data = await getOutreachDraft(route.params.id, token())
    draft.value = data
    subject.value = data.subject || ''
    recipient.value = data.recipient_email || ''
    // Saved name wins; otherwise fall back to the contact that owns the
    // recipient address, so drafts prepared before contact_name existed still
    // arrive filled in.
    contactName.value = data.contact_name || nameForEmail(recipient.value)
    crmStatusId.value = data.crm_status ? data.crm_status.id : null
    crmDetailId.value = data.crm_detail ? data.crm_detail.id : null
    sendMailboxId.value = data.send_mailbox_id || null
    crmDetailNotes.value = data.crm_detail_notes || ''
    // A draft that already carries a next action keeps it; otherwise fall back to
    // whatever the analyst last chose. Persisted on the pre-send save, so a draft
    // sent without touching these still applies the carried-over values.
    const sticky = readStickyNextAction()
    crmNextAction.value = data.crm_next_action || sticky.action
    crmNextActionDueDate.value = data.crm_next_action_due_date || sticky.dueDate
    addresses.cc = [...(data.cc || [])]
    addresses.bcc = [...(data.bcc || [])]
    addressDraft.cc = ''
    addressDraft.bcc = ''
    // Stay open if the draft already has any, so they aren't hidden from someone
    // reviewing a draft they didn't compose.
    showCcBcc.value = addresses.cc.length > 0 || addresses.bcc.length > 0
    includeSignature.value = data.include_signature !== false
    // Start from the analyst's saved copy if present, else the rendered baseline.
    body.value = data.final_body_html || data.body_html || ''
  } catch (e) {
    $toast.error('Failed to load draft')
  }
}

// Fires only on genuine editor edits (RichTextEditor does not emit on the
// initial programmatic content set), so loading a draft never autosaves.
function onBodyUpdate(html) {
  body.value = html
  scheduleSave()
}

// Saved straight away rather than debounced: the signature preview and the
// double-signature check both follow the mailbox.
function onFromChange() {
  if (saveTimer) clearTimeout(saveTimer)
  save()
}

function scheduleSave() {
  // A scheduled draft is locked; don't autosave (a save would auto-unschedule it
  // server-side). The UI blocks edits too, so this is a belt-and-braces guard.
  if (isLocked.value) return
  if (saveTimer) clearTimeout(saveTimer)
  saveTimer = setTimeout(save, 800)
}

// Returns whether the working copy reached the server, so send() can refuse to
// mail a draft whose latest state was never persisted.
async function save() {
  if (!draft.value || readOnly.value) return false
  saving.value = true
  try {
    const updated = await updateOutreachDraft(draft.value.id, {
      final_body_html: body.value,
      subject: subject.value,
      recipient_email: recipient.value,
      contact_name: contactName.value,
      crm_status_id: crmStatusId.value,
      crm_detail_id: crmDetailId.value,
      crm_detail_notes: crmDetailNotes.value,
      crm_next_action: crmNextAction.value,
      crm_next_action_due_date: crmNextActionDueDate.value,
      cc: addresses.cc,
      bcc: addresses.bcc,
      include_signature: includeSignature.value,
      // Only when there's a choice: the backend refuses a From the viewer
      // can't send from, and that must not fail every autosave.
      ...(fromOptions.value.length && sendMailboxId.value
        ? { send_mailbox_id: sendMailboxId.value } : {}),
    }, token())
    draft.value.status = updated.status
    draft.value.recipient_email = updated.recipient_email
    draft.value.contact_name = updated.contact_name
    draft.value.crm_status = updated.crm_status
    draft.value.crm_detail = updated.crm_detail
    draft.value.crm_detail_notes = updated.crm_detail_notes
    draft.value.crm_next_action = updated.crm_next_action
    draft.value.crm_next_action_due_date = updated.crm_next_action_due_date
    draft.value.cc = updated.cc
    draft.value.bcc = updated.bcc
    draft.value.include_signature = updated.include_signature
    // The From decides the signature, so both come back from the server.
    draft.value.send_mailbox_id = updated.send_mailbox_id
    draft.value.from_email = updated.from_email
    draft.value.signature_preview = updated.signature_preview
    draft.value.inline_images = updated.inline_images
    return true
  } catch (e) {
    $toast.error('Autosave failed')
    return false
  } finally {
    saving.value = false
  }
}

// Hard gate before an email actually goes out (now or scheduled): if the draft
// still holds the token markers the banners warn about, make the analyst confirm.
// Returns true to proceed. Kept as a blocking confirm on purpose -- it's the last
// stop before an unfilled {{field}} or [PLACEHOLDER] reaches a real recipient.
function confirmTokensBeforeSend() {
  const issues = []
  if (hasCurlyBraces.value) {
    issues.push(`• a curly brace { or } — an injectable field like ${exampleInjectableField} may not have been filled in`)
  }
  if (hasSquareBrackets.value) {
    issues.push('• a square bracket [ or ] — an AI placeholder like [PRODUCTS] may not have been replaced')
  }
  if (!issues.length) return true
  return window.confirm(
    `This draft still contains:\n\n${issues.join('\n')}\n\nSend it anyway?`
  )
}

async function send() {
  // The Send button is disabled without a subject; this backs that up.
  if (subjectMissing.value) {
    $toast.error('Add a subject before sending.')
    return
  }
  // Last-chance token check before the email leaves.
  if (!confirmTokensBeforeSend()) return
  // Always flush first, not just when an autosave is pending: the next action and
  // due date can be carried over from an earlier draft and not yet written, and
  // the send applies whatever the draft holds server-side.
  if (saveTimer) { clearTimeout(saveTimer); saveTimer = null }
  if (!await save()) {
    $toast.error('Could not save the draft — not sending. Check your connection and retry.')
    return
  }
  sending.value = true
  try {
    const updated = await sendOutreachDraft(draft.value.id, token())
    draft.value = { ...draft.value, ...updated }
    $toast.success('Sent')
    goBack()
  } catch (e) {
    $toast.error(e?.response?.data?.detail || 'Send failed')
  } finally {
    sending.value = false
  }
}

// Queue the draft to send at the picked instant. Flush the working copy first,
// same as send(), so what goes out later reflects the latest edits.
async function onConfirmSchedule(scheduledSendAt) {
  if (subjectMissing.value) {
    $toast.error('Add a subject before scheduling a send.')
    return
  }
  // A scheduled send goes out unattended, so the token check matters even more here.
  if (!confirmTokensBeforeSend()) return
  if (saveTimer) { clearTimeout(saveTimer); saveTimer = null }
  if (!await save()) {
    $toast.error('Could not save the draft — not scheduling. Check your connection and retry.')
    return
  }
  scheduling.value = true
  try {
    const updated = await scheduleOutreachDraft(draft.value.id, token(), scheduledSendAt)
    draft.value = { ...draft.value, ...updated }
    showScheduleModal.value = false
    $toast.success('Send scheduled')
    goBack()
  } catch (e) {
    $toast.error(e?.response?.data?.detail || 'Could not schedule the send')
  } finally {
    scheduling.value = false
  }
}

async function unschedule() {
  unscheduling.value = true
  try {
    const updated = await unscheduleOutreachDraft(draft.value.id, token())
    draft.value = { ...draft.value, ...updated }
    $toast.success('Schedule cancelled')
  } catch (e) {
    $toast.error(e?.response?.data?.detail || 'Could not cancel the schedule')
  } finally {
    unscheduling.value = false
  }
}

async function discard() {
  try {
    await discardOutreachDraft(draft.value.id, token())
    $toast.success('Discarded')
    goBack()
  } catch (e) {
    $toast.error('Discard failed')
  }
}

// From a discarded draft: make a fresh draft for the same company + template
// and open it. Navigating to a different draft id reuses this component, so the
// route watcher below reloads it.
async function createNew() {
  recreating.value = true
  try {
    const created = await recreateOutreachDraft(draft.value.id, token())
    $toast.success('New draft created')
    router.push(`/outreach-drafts/${created.id}`)
  } catch (e) {
    $toast.error(e?.response?.data?.detail || 'Could not create a new draft')
  } finally {
    recreating.value = false
  }
}

// From a sent draft: make a follow-up that replies within its thread and open it.
// Create the reply and open it for editing. `choice` is 'bespoke' (no template,
// empty body), null (reuse this draft's own template) or a template id.
async function onConfirmFollowUp(choice) {
  followingUp.value = true
  try {
    const created = choice === 'bespoke'
      ? await replyToOutreachDraft(draft.value.id, token())
      : await prepareFollowUpOutreachDraft(draft.value.id, token(), choice)
    showFollowUpModal.value = false
    $toast.success('Reply draft created')
    router.push(`/outreach-drafts/${created.id}`)
  } catch (e) {
    $toast.error(e?.response?.data?.detail || 'Could not create a reply')
  } finally {
    followingUp.value = false
  }
}

// Options are picked by index, not email -- the same address can appear under
// more than one contact, and we need the specific one to take the name from.
async function onPickRecipient(event) {
  const index = event.target.value
  const picked = index === '' ? null : recipientOptions.value[Number(index)]
  if (picked) {
    // Resolve the contact being replaced (from the current address) and the new
    // one BEFORE overwriting recipient, so the body swap has both sides.
    const oldContact = contactForEmail(recipient.value)
    const newContact = contactForEmail(picked.email)
    recipient.value = picked.email
    // Picking a contact retargets the draft, so the recorded name follows it.
    // Still free text: the analyst can overwrite it afterwards.
    contactName.value = picked.name || ''
    // Flip the previous contact's injected values (name, preferred name, title)
    // over to the new contact wherever they still appear in the body.
    if (oldContact && newContact && oldContact !== newContact) {
      const applied = swapContactFieldsInBody(oldContact, newContact)
      if (applied.length) {
        $toast.success('Updated contact details in the draft')
        // Wait for the editor to re-render the new body, then flash what changed.
        await nextTick()
        richEditor.value?.flashValues(applied)
      }
    }
    scheduleSave()
  }
  // reset the picker back to its placeholder; the input is the source of truth
  event.target.value = ''
}

async function generateSuggestions() {
  suggesting.value = true
  try {
    const res = await generateOutreachSuggestions(draft.value.id, body.value, token())
    suggestions.value = res.suggestions || []
    suggested.value = true
  } catch (e) {
    $toast.error('Failed to generate suggestions')
  } finally {
    suggesting.value = false
  }
}

async function copy(value) {
  if (!value) return
  try {
    if (navigator.clipboard && navigator.clipboard.writeText) {
      await navigator.clipboard.writeText(value)
    } else {
      const ta = document.createElement('textarea')
      ta.value = value
      document.body.appendChild(ta)
      ta.select()
      document.execCommand('copy')
      document.body.removeChild(ta)
    }
    $toast.success('Copied')
  } catch (e) {
    $toast.error('Copy failed')
  }
}

function escapeHtml(text) {
  return String(text)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
}

// Insert a chosen candidate into the draft body in place of its [TOKEN].
// Matches on the token's leading keyword so it still lands on tokens carrying
// inline guidance, e.g. [PRODUCTS - focus on landing gear]. Editing the body
// value flows into TipTap via its modelValue watch (it re-syncs while unfocused,
// which it is once the panel button is clicked).
function insertSuggestion(suggestion, candidate) {
  if (!candidate) return
  const m = (suggestion && suggestion.token ? suggestion.token : '').match(/[A-Za-z_]+/)
  const keyword = m ? m[0] : ''
  if (!keyword) { copy(candidate); return }
  // [KEYWORD], [KEYWORD - guide], [KEYWORD: guide], or [KEYWORD guide]
  const re = new RegExp('\\[\\s*' + keyword + '\\b[^\\]]*\\]', 'i')
  const current = body.value || ''
  if (!re.test(current)) {
    // Token not present (already filled, or edited away) — don't silently lose it.
    copy(candidate)
    $toast.warning('Placeholder no longer in the body — copied instead')
    return
  }
  body.value = current.replace(re, escapeHtml(candidate))
  scheduleSave()
  $toast.success('Inserted')
}

// Back to the queue on the tab we came from (?tab=, set by the queue when it
// opens a draft). Without it the queue falls back to its default tab.
function goBack() {
  // Opened from a thread: ?from= is that thread's page, so Back returns there.
  // Only same-origin relative paths, so a hand-edited ?from= can't send a user
  // off-site -- the same guard as the thread page's own Back.
  const from = route.query.from
  if (typeof from === 'string' && from.startsWith('/') && !from.startsWith('//')) {
    router.push(from)
    return
  }
  // The whole query goes back, not just the tab: it carries the queue's filter
  // state, and dropping it would land the analyst on an unfiltered list.
  router.push({ path: '/outreach-drafts', query: route.query })
}

function viewSent() {
  // Same reason: the preview's own Back has to be able to restore the queue.
  router.push({ path: `/outreach-drafts/${draft.value.id}/preview`, query: route.query })
}

const statusLabels = {
  draft: 'Draft',
  in_progress: 'In progress',
  ready: 'Ready',
  scheduled: 'Scheduled',
  sent: 'Sent',
  blocked: 'No recipient',
  failed: 'Failed',
  discarded: 'Discarded',
}

function statusLabel(status) {
  return statusLabels[status] || status
}

function statusClass(status) {
  switch (status) {
    case 'sent': return 'bg-green-100 text-green-800'
    case 'scheduled': return 'bg-indigo-100 text-indigo-800'
    case 'ready': return 'bg-blue-100 text-blue-800'
    case 'in_progress': return 'bg-yellow-100 text-yellow-800'
    case 'blocked':
    case 'failed': return 'bg-red-100 text-red-800'
    default: return 'bg-gray-100 text-gray-700'
  }
}

onBeforeUnmount(() => {
  if (saveTimer) clearTimeout(saveTimer)
})

async function loadCrmStatuses() {
  try {
    crmStatuses.value = await getCrmStatuses(token())
  } catch (e) {
    // the draft is still editable/sendable without the picker populated
    $toast.error('Failed to load CRM statuses')
  }
}

async function loadCrmActionTypes() {
  try {
    // Unpaginated list, ordinal-ordered by the server -- same source the email
    // template form's CRM Detail picker uses.
    const res = await getActions(token())
    crmActionTypes.value = (res && res.data) || []
  } catch (e) {
    // Non-fatal: the draft keeps the detail it was seeded with and still sends.
    $toast.error('Failed to load CRM details')
  }
}

// Navigating between draft ids (e.g. "Create new draft") reuses this component
// instance, so onMounted won't refire -- reload explicitly on id change.
watch(() => route.params.id, () => {
  if (saveTimer) { clearTimeout(saveTimer); saveTimer = null }
  load()
})

onMounted(() => {
  load()
  loadCrmStatuses()
  loadCrmActionTypes()
})
</script>

<style scoped>
/* A short lean-in, then hold a soft ring for as long as Send is hovered. Small
   on purpose: enough to catch the eye at the edge of vision, not enough to
   distract someone who is re-reading their email before sending. */
.crm-armed {
    animation: crm-nudge 360ms ease-out;
    box-shadow: 0 0 0 3px rgb(99 102 241 / 0.18);
}

@keyframes crm-nudge {
    0%   { transform: translateX(0); }
    35%  { transform: translateX(-3px); }
    70%  { transform: translateX(2px); }
    100% { transform: translateX(0); }
}

/* Someone who has asked for less motion still gets the ring, just no movement. */
@media (prefers-reduced-motion: reduce) {
    .crm-armed { animation: none; }
}

/* The email being replied to, as the thread view renders one. */
.parent-email-body :deep(img) { max-width: 100%; height: auto; }
.parent-email-body :deep(a) { color: #2563eb; text-decoration: underline; }
.parent-email-body :deep(p) { margin: 0 0 0.5rem; }
.parent-email-body :deep(pre) { white-space: pre-wrap; word-break: break-word; font-size: 0.75rem; margin: 0; }
</style>
